// Product images are uploaded in three widths. The main file is "<name>_w1600.<ext>" and
// its smaller siblings are "<name>_w800.<ext>" and "<name>_w400.<ext>".
// Images uploaded before this convention are single, often full-size files. On Netlify those
// are resized on request by Netlify Image CDN (allowed in netlify.toml), which fetches the
// original from Supabase once and caches each size at its edge.

export const PRODUCT_IMAGE_WIDTHS = [1600, 800, 400];
export const PRODUCT_IMAGE_MAX_WIDTH = PRODUCT_IMAGE_WIDTHS[0];

const VARIANT_PATTERN = /_w1600\.(webp|jpg)(\?.*)?$/;
const STORAGE_MARKER = '/storage/v1/object/public/VanBelle/';
// Same quality levels as the upload step (src/utils/imageProcessing.js)
const CDN_QUALITY_BY_WIDTH = { 1920: 85, 1600: 85, 800: 83, 400: 82 };

// /.netlify/images only exists on Netlify, so default to production builds only.
// Set REACT_APP_IMAGE_CDN=false to turn it off, or =true to force it on (e.g. under `netlify dev`).
const IMAGE_CDN_SETTING = process.env.REACT_APP_IMAGE_CDN;
const IMAGE_CDN_ENABLED = IMAGE_CDN_SETTING === 'true' || IMAGE_CDN_SETTING === 'false'
  ? IMAGE_CDN_SETTING === 'true'
  : process.env.NODE_ENV === 'production';

export const hasImageVariants = (url) => typeof url === 'string' && VARIANT_PATTERN.test(url);

const isStorageImage = (url) => typeof url === 'string' && url.includes(STORAGE_MARKER);

// Resized copy of a single-file storage image via Netlify Image CDN (format picked per browser, WebP/AVIF)
export const getResizedImageUrl = (url, width) => {
  if (!isStorageImage(url) || !IMAGE_CDN_ENABLED) return url;
  const quality = CDN_QUALITY_BY_WIDTH[width] || 85;
  return `/.netlify/images?url=${encodeURIComponent(url)}&w=${width}&q=${quality}`;
};

// Banners are shown full-width. New uploads are already 1920px WebP; older ones get resized.
export const getBannerImageUrl = (url) => (
  typeof url === 'string' && /\.webp(\?.*)?$/.test(url) ? url : getResizedImageUrl(url, 1920)
);

// URL of the size closest to (and at least) `width` pixels wide
export const getImageVariant = (url, width) => {
  const size = [...PRODUCT_IMAGE_WIDTHS].reverse().find((w) => w >= width) || PRODUCT_IMAGE_MAX_WIDTH;
  if (!hasImageVariants(url)) return getResizedImageUrl(url, size);
  return size === PRODUCT_IMAGE_MAX_WIDTH ? url : url.replace(VARIANT_PATTERN, `_w${size}.$1$2`);
};

export const getImageSrcSet = (url) => {
  if (!hasImageVariants(url) && getResizedImageUrl(url, PRODUCT_IMAGE_MAX_WIDTH) === url) return undefined;
  return PRODUCT_IMAGE_WIDTHS.map((w) => `${getImageVariant(url, w)} ${w}w`).join(', ');
};

// Storage object paths for an image and its variants, e.g. ["products/x_w1600.webp", ...]
export const getVariantPaths = (path) => {
  const match = path.match(/^(.*)_w1600\.(webp|jpg)$/);
  if (!match) return [path];
  return PRODUCT_IMAGE_WIDTHS.map((w) => `${match[1]}_w${w}.${match[2]}`);
};
